import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Objective, ObjectiveEntry } from 'services/objectives/types';
import { goalKindOf } from '../ObjectivesViewer/utils';
import { buildProgressChart } from './progressChart';
import styles from './ObjectiveProgressChart.module.css';

interface ObjectiveProgressChartProps {
  entries: ObjectiveEntry[];
  objective: Objective;
}

const ObjectiveProgressChart = ({
  entries,
  objective,
}: ObjectiveProgressChartProps) => {
  const clipId = useId().replace(/:/g, '');
  const plotRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [plotWidth, setPlotWidth] = useState<number>();
  const kind = goalKindOf(objective);
  const chart = buildProgressChart({
    entries,
    goal: objective.goal,
    goalTolerance: kind === 'AROUND' ? objective.goalTolerance : null,
    targets: objective.targets,
    width: plotWidth,
  });
  const hasChart = chart !== null;

  useLayoutEffect(() => {
    const element = plotRef.current;
    if (!element) {
      return undefined;
    }
    const update = () => {
      const next = Math.round(element.clientWidth);
      if (next > 0) {
        setPlotWidth(current => (current === next ? current : next));
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasChart]);

  if (!chart) {
    return null;
  }

  const active = chart.points.find(point => point.id === activeId) ?? null;
  const activeRatio = active ? active.x / chart.width : 0;
  const tooltipShiftX = activeRatio < 0.2 ? '0' : activeRatio > 0.8 ? '-100%' : '-50%';
  const tooltipShiftY = active && active.y < chart.plot.y + 36 ? '12px' : 'calc(-100% - 10px)';
  const line = chart.points.map(point => `${point.x},${point.y}`).join(' ');

  return (
    <figure
      aria-label={t('progressOverTime')}
      className={styles.chart}
    >
      <div className={styles.plot} ref={plotRef}>
        <svg
          aria-hidden="true"
          preserveAspectRatio="none"
          style={{ height: chart.height }}
          viewBox={`0 0 ${chart.width} ${chart.height}`}
        >
          <defs>
            <clipPath id={clipId}>
              <rect
                height={chart.plot.height}
                width={chart.plot.width}
                x={chart.plot.x}
                y={chart.plot.y}
              />
            </clipPath>
          </defs>
          {
            chart.band && (
              <rect
                className={styles.band}
                height={chart.band.height}
                width={chart.plot.width}
                x={chart.plot.x}
                y={chart.band.y}
              />
            )
          }
          {
            chart.yTicks.map(tick => (
              <line
                className={styles.grid}
                key={`y-${tick.label}-${tick.y}`}
                x1={chart.plot.x}
                x2={chart.plot.x + chart.plot.width}
                y1={tick.y}
                y2={tick.y}
              />
            ))
          }
          {
            chart.goalY != null && objective.goal != null && (
              <g>
                <line
                  className={styles.goal}
                  x1={chart.plot.x}
                  x2={chart.plot.x + chart.plot.width}
                  y1={chart.goalY}
                  y2={chart.goalY}
                />
                <text
                  className={styles.goalLabel}
                  textAnchor="end"
                  x={chart.plot.x + chart.plot.width - 4}
                  y={chart.goalY <= chart.plot.y + 16 ? chart.goalY + 14 : chart.goalY - 6}
                >
                  {objective.goal}
                </text>
              </g>
            )
          }
          {
            chart.points.length > 1 && (
              <polyline
                className={styles.series}
                clipPath={`url(#${clipId})`}
                points={line}
              />
            )
          }
          {
            chart.targets.map(series => (
              series.points.length > 1 && (
                <polyline
                  className={styles.targetSeries}
                  clipPath={`url(#${clipId})`}
                  key={series.id}
                  points={series.points.map(point => `${point.x},${point.y}`).join(' ')}
                  stroke={series.color}
                />
              )
            ))
          }
          {
            chart.targets.flatMap(series => (
              series.points.map((point, index) => (
                <circle
                  cx={point.x}
                  cy={point.y}
                  fill={series.color}
                  key={`${series.id}-${index}`}
                  r={3}
                />
              ))
            ))
          }
          {
            chart.points.map(point => (
              <circle
                className={`${styles.point} ${point.id === activeId ? styles.pointActive : ''}`}
                cx={point.x}
                cy={point.y}
                key={point.id}
                r={point.id === activeId ? 5 : 3.5}
              />
            ))
          }
          {
            chart.yTicks.map(tick => (
              <text
                className={styles.axis}
                dominantBaseline="middle"
                key={`yl-${tick.label}-${tick.y}`}
                textAnchor="end"
                x={chart.plot.x - 8}
                y={tick.y}
              >
                {tick.label}
              </text>
            ))
          }
          {
            chart.xTicks.map((tick, index) => (
              <text
                className={styles.axis}
                dominantBaseline="hanging"
                key={`x-${tick.label}-${tick.x}`}
                textAnchor={chart.xTicks.length === 1 ? 'middle' : index === 0 ? 'start' : index === chart.xTicks.length - 1 ? 'end' : 'middle'}
                x={tick.x}
                y={tick.y + 8}
              >
                {tick.label}
              </text>
            ))
          }
        </svg>
        <div className={styles.points}>
          {
            chart.points.map(point => (
              <button
                aria-label={t('progressChartPoint', { date: point.label, value: point.value })}
                className={styles.hit}
                key={point.id}
                onBlur={() => setActiveId(current => (current === point.id ? null : current))}
                onFocus={() => setActiveId(point.id)}
                onMouseEnter={() => setActiveId(point.id)}
                onMouseLeave={() => setActiveId(current => (current === point.id ? null : current))}
                style={{
                  left: `${(point.x / chart.width) * 100}%`,
                  top: `${(point.y / chart.height) * 100}%`,
                }}
                type="button"
              />
            ))
          }
        </div>
        {
          active && (
            <div
              className={styles.tooltip}
              role="tooltip"
              style={{
                left: `${activeRatio * 100}%`,
                top: `${(active.y / chart.height) * 100}%`,
                transform: `translate(${tooltipShiftX}, ${tooltipShiftY})`,
              }}
            >
              {active.label}
              <br />
              {active.value}
              {
                active.note && (
                  <>
                    <br />
                    {active.note}
                  </>
                )
              }
            </div>
          )
        }
      </div>
      {
        chart.targets.length > 0 && (
          <ul className={styles.legend}>
            {
              chart.points.length > 0 && (
                <li>
                  <span className={styles.swatch} style={{ background: '#5b995b' }} />
                  {t('progress')}
                </li>
              )
            }
            {
              chart.targets.map(series => (
                <li key={series.id}>
                  <span className={styles.swatch} style={{ background: series.color }} />
                  {series.name}
                </li>
              ))
            }
          </ul>
        )
      }
    </figure>
  );
};

export default ObjectiveProgressChart;
